import {
  YoutubeLinkRepository,
  YoutubeLinkFilters,
} from '../../domain/repositories/youtube-link.repository.js';
import { YoutubeLinkEntity, YoutubeLinkProps } from '../../domain/entities/youtube-link.entity.js';
import { IHttpClient } from '../../application/interfaces/http-client.interface.js';

interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

export class HttpYoutubeLinkRepository implements YoutubeLinkRepository {
  constructor(private readonly httpClient: IHttpClient) {}

  public async getAll(options?: YoutubeLinkFilters): Promise<YoutubeLinkEntity[]> {
    const endpoint = options?.activeOnly ? '/youtube-links/public' : '/youtube-links';
    const params = new URLSearchParams();

    if (options?.search?.trim()) {
      params.append('search', options.search.trim());
    }

    const query = params.toString() ? `?${params.toString()}` : '';
    const res = await this.httpClient.get<ApiResponse<YoutubeLinkProps[]>>(`${endpoint}${query}`);

    if (!res.success || !res.data) {
      return [];
    }

    return res.data.map((item) => new YoutubeLinkEntity(item));
  }

  public async getById(id: string): Promise<YoutubeLinkEntity> {
    const res = await this.httpClient.get<ApiResponse<YoutubeLinkProps>>(`/youtube-links/${encodeURIComponent(id)}`);
    if (!res.success || !res.data) {
      throw new Error(res.message || 'Video no encontrado');
    }
    return new YoutubeLinkEntity(res.data);
  }

  public async create(
    data: Omit<YoutubeLinkProps, 'id' | 'createdAt' | 'updatedAt' | 'videoId' | 'thumbnailUrl'>
  ): Promise<YoutubeLinkEntity> {
    const res = await this.httpClient.post<ApiResponse<YoutubeLinkProps>>('/youtube-links', data);
    if (!res.success || !res.data) {
      throw new Error(res.message || 'Error al registrar el video');
    }
    return new YoutubeLinkEntity(res.data);
  }

  public async update(
    id: string,
    data: Partial<Omit<YoutubeLinkProps, 'id' | 'createdAt' | 'updatedAt' | 'videoId' | 'thumbnailUrl'>>
  ): Promise<YoutubeLinkEntity> {
    const res = await this.httpClient.put<ApiResponse<YoutubeLinkProps>>(
      `/youtube-links/${encodeURIComponent(id)}`,
      data
    );
    if (!res.success || !res.data) {
      throw new Error(res.message || 'Error al actualizar el video');
    }
    return new YoutubeLinkEntity(res.data);
  }

  public async toggleStatus(id: string, isActive: boolean): Promise<YoutubeLinkEntity> {
    const res = await this.httpClient.patch<ApiResponse<YoutubeLinkProps>>(
      `/youtube-links/${encodeURIComponent(id)}/status`,
      { isActive }
    );
    if (!res.success || !res.data) {
      throw new Error(res.message || 'Error al cambiar estado del video');
    }
    return new YoutubeLinkEntity(res.data);
  }

  public async delete(id: string): Promise<void> {
    const res = await this.httpClient.delete<ApiResponse<void>>(`/youtube-links/${encodeURIComponent(id)}`);
    if (!res.success) {
      throw new Error(res.message || 'Error al eliminar el video');
    }
  }
}
